"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Zap, Moon } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function HighPriestessCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">The High Priestess</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The High Priestess</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-high-priestess.svg"
                  alt="The High Priestess Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">II</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The High Priestess</h1>
                  <p className="text-xl text-blue-400">Yüksek Rahibe / Sezgi</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Sezginin, iç bilgeliğin ve gizli bilginin kartı. Bilinçdışı alanların
                ve ruhsal derinliklerin bekçisi olan figür, sessizlik ve içsel dinleme gücünü temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Sezgi</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">İç bilgelik</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Gizli bilgi</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Sessizlik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlü Sezgi</h3>
                  <p className="text-slate-400 text-sm">İç sesini dinleme ve doğru kararlar alma yeteneği.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Bilgelik</h3>
                  <p className="text-slate-400 text-sm">Derin anlayış, esoterik bilgi ve ruhsal gelişim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gizli Potansiyel</h3>
                  <p className="text-slate-400 text-sm">Henüz ortaya çıkmayan yetenekler ve fırsatlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabır ve Bekleme</h3>
                  <p className="text-slate-400 text-sm">Doğru zamanı bekleme ve sabırlı olma erdemi.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sezgileri Yok Sayma</h3>
                  <p className="text-slate-400 text-sm">İç sesini dinlememe, mantığa aşırı güvenme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gizlilik Obsesyonu</h3>
                  <p className="text-slate-400 text-sm">Aşırı ketumlanma, bilgiyi saklama, izolasyon.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Blokaj</h3>
                  <p className="text-slate-400 text-sm">Spiritüel bağlantıdan kopma, içsel karışıklık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Pasiflik</h3>
                  <p className="text-slate-400 text-sm">Eyleme geçmeme, fırsatları kaçırma, durgunluk.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <Moon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ay Sembolü</h3>
              <p className="text-slate-400 text-sm">Bilinçdışı, sezgi, dişil enerji ve gizli bilgi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🔮</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kristal Küre</h3>
              <p className="text-slate-400 text-sm">Geleceği görme, kehanet ve ruhsal netlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">📖</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kutsal Kitap</h3>
              <p className="text-slate-400 text-sm">Gizli bilgi, ruhsal öğretiler ve kadim hikmet.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏛️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Tapınak Sütunları</h3>
              <p className="text-slate-400 text-sm">Bilgi ve cehaletten oluşan dualite, kapı bekçisi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌊</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Su</h3>
              <p className="text-slate-400 text-sm">Bilinçdışı akışlar, duygusal derinlik ve temizlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-gray-800 text-lg">👑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taç</h3>
              <p className="text-slate-400 text-sm">Ruhsal otorite, bilgelik tacı ve divine bağlantı.</p>
            </div>
          </div>
        </div>

        {/* Lunar Phases */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ay Evreleri ve Anlamları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-900/20 rounded-lg border border-blue-700/30">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌑</span>
              </div>
              <h3 className="font-semibold text-blue-200 mb-2">Yeni Ay</h3>
              <p className="text-blue-300 text-xs">Yeni başlangıçlar, içsel yolculuk</p>
            </div>
            <div className="text-center p-4 bg-indigo-900/20 rounded-lg border border-indigo-700/30">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌓</span>
              </div>
              <h3 className="font-semibold text-indigo-200 mb-2">İlk Dördün</h3>
              <p className="text-indigo-300 text-xs">Manifestasyon, büyüme, gelişim</p>
            </div>
            <div className="text-center p-4 bg-purple-900/20 rounded-lg border border-purple-700/30">
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌕</span>
              </div>
              <h3 className="font-semibold text-purple-200 mb-2">Dolunay</h3>
              <p className="text-purple-300 text-xs">Zirve, tamamlanma, aydınlanma</p>
            </div>
            <div className="text-center p-4 bg-slate-800/20 rounded-lg border border-slate-600/30">
              <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌗</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Son Dördün</h3>
              <p className="text-slate-300 text-xs">Bırakma, temizlik, yeniden doğum</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Derin duygusal bağ</li>
                <li>• Gizli aşk veya platonik ilişki</li>
                <li>• Sezgisel partner seçimi</li>
                <li>• Ruhsal bağlantı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Psikoloji, danışmanlık alanları</li>
                <li>• Sanat ve yaratıcılık işleri</li>
                <li>• Araştırma ve akademi</li>
                <li>• Sezgiye dayalı kararlar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Moon className="h-5 w-5 text-blue-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Meditasyon ve iç gözlem</li>
                <li>• Ruhsal pratikler</li>
                <li>• Psişik yeteneklerin gelişimi</li>
                <li>• İç bilgeliğe erişim</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-400" />
                Sağlık ve Enerji
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Hormonal dengelenme</li>
                <li>• Stres azaltma teknikleri</li>
                <li>• Uyku kalitesini artırma</li>
                <li>• Duygusal iyileşme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/magician"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Magician
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/empress"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            The Empress
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}